import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Box,
  Text,
  Badge,
  Divider,
  Card,
  CardBody,
  CardHeader,
  SimpleGrid,
  Spinner,
  Alert,
  AlertIcon,
  Icon,
  Flex,
  useColorModeValue,
  Link,
  Skeleton,
  SkeletonText,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from '@chakra-ui/react';
import { MdPerson, MdPhone, MdEmail, MdLocationOn, MdWork, MdGavel, MdOpenInNew } from 'react-icons/md';
import { getAdvogadoDetails, formatCPF, formatOAB } from '../../../../../../libs/advogado/advogadoUtils';

const AdvogadoDetailsModal = ({ isOpen, onClose, advogado, estado = 'MT' }) => {
  const [loading, setLoading] = useState(false);
  const [advogadoDetails, setAdvogadoDetails] = useState(null);
  const [error, setError] = useState(null);

  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const headingColor = useColorModeValue('gray.800', 'white');
  const tableHeaderBg = useColorModeValue('gray.50', 'gray.700');

  // Format date to Brazilian format
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      // Handle Brazilian date format (DD/MM/YYYY HH:mm:ss)
      if (dateString.includes('/')) {
        const [datePart] = dateString.split(' ');
        const [day, month, year] = datePart.split('/');
        const date = new Date(year, month - 1, day);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('pt-BR');
        }
      }
      // Handle ISO format
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('pt-BR');
      }
      return dateString;
    } catch {
      return dateString;
    }
  };

  useEffect(() => {
    if (isOpen && advogado) {
      fetchDetails();
    }
  }, [isOpen, advogado]);

  const fetchDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const details = await getAdvogadoDetails(advogado, estado);
      setAdvogadoDetails(details);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const InfoField = ({ label, value, icon }) => (
    <Box>
      <HStack spacing={2} mb={1}>
        {icon && <Icon as={icon} color={textColor} />}
        <Text fontSize="sm" color={textColor} fontWeight="medium">
          {label}
        </Text>
      </HStack>
      <Text fontSize="md" ml={icon ? 6 : 0}>
        {value || '-'}
      </Text>
    </Box>
  );

  const handleOpenCNA = () => {
    window.open('https://cna.oab.org.br/', '_blank');
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader bg={tableHeaderBg} borderTopRadius="md">
          <Flex align="center" justify="space-between">
            <Text>Detalhes do Advogado</Text>
            {advogado && (
              <Badge colorScheme="blue" fontSize="sm">
                {formatOAB(advogado.oabs) || 'Sem OAB'}
              </Badge>
            )}
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody py={4}>
          {loading ? (
            <VStack spacing={4} py={8}>
              <Spinner size="xl" thickness="4px" />
              <Text>Carregando informações do advogado...</Text>
              <Text fontSize="sm" color={textColor}>
                Consultando base de dados...
              </Text>
            </VStack>
          ) : error ? (
            <Alert status="error" variant="left-accent">
              <AlertIcon />
              <VStack align="start" spacing={2}>
                <Text fontWeight="bold">Erro ao carregar informações</Text>
                <Text>{error}</Text>
              </VStack>
            </Alert>
          ) : advogadoDetails ? (
            <VStack spacing={4} align="stretch">
              {/* Basic Information Card */}
              <Card variant="outline" borderColor={borderColor} bg={cardBg}>
                <CardHeader pb={2}>
                  <HStack spacing={2}>
                    <Icon as={MdPerson} size="lg" />
                    <Text fontSize="lg" fontWeight="bold">Informações Pessoais</Text>
                  </HStack>
                </CardHeader>
                <Divider />
                <CardBody>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <InfoField 
                      label="Nome Completo" 
                      value={advogadoDetails.nome}
                      icon={MdPerson}
                    />
                    <InfoField 
                      label="CPF" 
                      value={formatCPF(advogadoDetails.cpf)}
                    />
                    <InfoField 
                      label="Tipo de Pessoa" 
                      value={advogadoDetails.tipo_pessoa}
                    />
                    <InfoField 
                      label="Quantidade de Processos" 
                      value={`${advogadoDetails.quantidade_processos} processos`}
                      icon={MdGavel}
                    />
                    {advogadoDetails.cna && (
                      <>
                        <InfoField 
                          label="Data de Nascimento" 
                          value={advogadoDetails.cna.data_nascimento ? formatDate(advogadoDetails.cna.data_nascimento) : '-'}
                        />
                        <InfoField 
                          label="Idade" 
                          value={advogadoDetails.cna.idade ? `${advogadoDetails.cna.idade} anos` : '-'}
                        />
                        <InfoField 
                          label="Sexo" 
                          value={advogadoDetails.cna.sexo || '-'}
                        />
                        <InfoField 
                          label="Nome da Mãe" 
                          value={advogadoDetails.cna.nome_mae || '-'}
                        />
                        <InfoField 
                          label="Signo" 
                          value={advogadoDetails.cna.signo || '-'}
                        />
                      </>
                    )}
                  </SimpleGrid>
                </CardBody>
              </Card>

              {/* OAB Information */}
              {advogadoDetails.oabs && advogadoDetails.oabs.length > 0 && (
                <Card variant="outline" borderColor={borderColor} bg={cardBg}>
                  <CardHeader pb={2}>
                    <Text fontSize="lg" fontWeight="bold">Inscrições OAB</Text>
                  </CardHeader>
                  <Divider />
                  <CardBody>
                    <VStack spacing={3} align="stretch">
                      {advogadoDetails.oabs.map((oab, index) => (
                        <Box key={index} p={3} border="1px" borderColor={borderColor} borderRadius="md">
                          <HStack justify="space-between">
                            <Box>
                              <Text fontWeight="bold">
                                OAB {oab.uf} {oab.numero}
                              </Text>
                              <Text fontSize="sm" color={textColor}>
                                Tipo: {oab.tipo || 'Principal'}
                              </Text>
                            </Box>
                            <Badge colorScheme="green">Ativo</Badge>
                          </HStack>
                        </Box>
                      ))}
                    </VStack>
                  </CardBody>
                </Card>
              )}

              {/* CNA Data (when available) */}
              {advogadoDetails.cnaSuccess && advogadoDetails.cna && (
                <>
                  {/* Contact Information */}
                  <Card variant="outline" borderColor={borderColor} bg={cardBg}>
                  <CardHeader pb={2}>
                  <HStack spacing={2}>
                  <Icon as={MdLocationOn} size="lg" />
                  <Text fontSize="lg" fontWeight="bold">Informações de Contato</Text>
                  </HStack>
                  </CardHeader>
                  <Divider />
                  <CardBody>
                  <VStack spacing={4} align="stretch">
                  {advogadoDetails.cna.endereco && (
                      <InfoField 
                        label="Endereço Principal" 
                        value={[
                        advogadoDetails.cna.endereco.logradouro,
                      advogadoDetails.cna.endereco.numero,
                    advogadoDetails.cna.endereco.complemento,
                    advogadoDetails.cna.endereco.bairro,
                      advogadoDetails.cna.endereco.cidade,
                        advogadoDetails.cna.endereco.estado
                        ].filter(Boolean).join(', ')}
                        icon={MdLocationOn}
                      />
                    )}
                    {advogadoDetails.cna.endereco?.cep && (
                      <InfoField 
                        label="CEP" 
                        value={advogadoDetails.cna.endereco.cep}
                      />
                    )}
                    {/* Show all addresses if we have the array */}
                    {advogadoDetails.cna._original?.enderecos && advogadoDetails.cna._original.enderecos.length > 0 && (
                      <Box>
                        <HStack spacing={2} mb={1}>
                          <Icon as={MdLocationOn} color={textColor} />
                          <Text fontSize="sm" color={textColor} fontWeight="medium">
                            Todos os Endereços ({advogadoDetails.cna._original.enderecos.length})
                          </Text>
                        </HStack>
                        <VStack align="start" spacing={2} ml={6}>
                          {advogadoDetails.cna._original.enderecos.map((end, idx) => (
                            <Box key={idx} p={2} border="1px" borderColor={borderColor} borderRadius="md" w="full">
                              <Text fontSize="sm" fontWeight="medium">
                                {[
                                  end.logradouro,
                                  end.numero,
                                  end.complemento
                                ].filter(Boolean).join(', ')}
                              </Text>
                              <Text fontSize="sm" color={textColor}>
                                {[
                                  end.bairro,
                                  end.cidade,
                                  end.uf
                                ].filter(Boolean).join(' - ')}
                              </Text>
                              {end.cep && (
                                <Text fontSize="xs" color={textColor}>
                                  CEP: {end.cep}
                                </Text>
                              )}
                            </Box>
                          ))}
                        </VStack>
                      </Box>
                    )}
                  {advogadoDetails.cna.emails && advogadoDetails.cna.emails.length > 0 && (
                  <Box>
                  <HStack spacing={2} mb={1}>
                  <Icon as={MdEmail} color={textColor} />
                    <Text fontSize="sm" color={textColor} fontWeight="medium">
                        E-mails ({advogadoDetails.cna.emails.length})
                        </Text>
                        </HStack>
                        <VStack align="start" spacing={1} ml={6}>
                          {advogadoDetails.cna.emails.map((email, idx) => (
                            <Text key={idx} fontSize="md">
                              {typeof email === 'string' ? email : email.enderecoEmail}
                            </Text>
                          ))}
                        </VStack>
                      </Box>
                    )}
                    {advogadoDetails.cna.telefones && advogadoDetails.cna.telefones.length > 0 && (
                      <Box>
                        <HStack spacing={2} mb={1}>
                          <Icon as={MdPhone} color={textColor} />
                          <Text fontSize="sm" color={textColor} fontWeight="medium">
                            Telefones
                          </Text>
                        </HStack>
                        <VStack align="start" spacing={1} ml={6}>
                          {(advogadoDetails.cna.telefones_completos || advogadoDetails.cna.telefones).map((tel, idx) => (
                            <Box key={idx} p={2} border="1px" borderColor={borderColor} borderRadius="md" w="full">
                              <HStack justify="space-between">
                                <Text fontSize="md" fontWeight="medium">
                                  {typeof tel === 'string' ? tel : tel.telefoneComDDD}
                                </Text>
                                {typeof tel === 'object' && (
                                  <HStack spacing={2}>
                                    {tel.whatsApp && <Badge colorScheme="green" size="sm">WhatsApp</Badge>}
                                    {tel.telemarketingBloqueado && <Badge colorScheme="red" size="sm">Bloqueado</Badge>}
                                  </HStack>
                                )}
                              </HStack>
                              {typeof tel === 'object' && (
                                <HStack spacing={2} mt={1}>
                                  {tel.operadora && <Badge variant="subtle" size="sm">{tel.operadora}</Badge>}
                                  {tel.tipoTelefone && <Badge variant="outline" size="sm">{tel.tipoTelefone}</Badge>}
                                </HStack>
                              )}
                            </Box>
                          ))}
                        </VStack>
                      </Box>
                    )}
                  </VStack>
                </CardBody>
              </Card>

                  {/* Professional Information */}
                  {advogadoDetails.cna.sociedades && advogadoDetails.cna.sociedades.length > 0 && (
                    <Card variant="outline" borderColor={borderColor} bg={cardBg}>
                      <CardHeader pb={2}>
                        <HStack spacing={2}>
                          <Icon as={MdWork} size="lg" />
                          <Text fontSize="lg" fontWeight="bold">Sociedades de Advogados</Text>
                        </HStack>
                      </CardHeader>
                      <Divider />
                      <CardBody>
                        <VStack spacing={2} align="stretch">
                          {advogadoDetails.cna.sociedades.map((soc, index) => (
                            <Text key={index}>{soc}</Text>
                          ))}
                        </VStack>
                      </CardBody>
                    </Card>
                  )}
                  
                  {/* Financial Information */}
                  {(advogadoDetails.cna.renda_estimada || advogadoDetails.cna.renda_faixa_salarial) && (
                    <Card variant="outline" borderColor={borderColor} bg={cardBg}>
                      <CardHeader pb={2}>
                        <Text fontSize="lg" fontWeight="bold">Informações Financeiras</Text>
                      </CardHeader>
                      <Divider />
                      <CardBody>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                          {advogadoDetails.cna.renda_estimada && (
                            <InfoField 
                              label="Renda Estimada" 
                              value={`R$ ${advogadoDetails.cna.renda_estimada}`}
                            />
                          )}
                          {advogadoDetails.cna.renda_faixa_salarial && (
                            <InfoField 
                              label="Faixa Salarial" 
                              value={advogadoDetails.cna.renda_faixa_salarial}
                            />
                          )}
                        </SimpleGrid>
                      </CardBody>
                    </Card>
                  )}
                </>
              )}

              {/* CNA Message/Warning */}
              {advogadoDetails.cnaMessage && (
                <Alert status="info" variant="left-accent">
                  <AlertIcon />
                  <Box>
                    <Text fontWeight="bold">Informação</Text>
                    <Text fontSize="sm">{advogadoDetails.cnaMessage}</Text>
                  </Box>
                </Alert>
              )}
            </VStack>
          ) : (
            <Text>Nenhuma informação disponível</Text>
          )}
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3}>
            <Button
              leftIcon={<Icon as={MdOpenInNew} />}
              colorScheme="blue"
              variant="outline"
              onClick={handleOpenCNA}
              size="sm"
            >
              Consultar no CNA
            </Button>
            <Button onClick={onClose}>Fechar</Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AdvogadoDetailsModal;
